import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiError } from '../../lib/api.ts';
import { StartModeField, type StartMode } from '../../components/StartModeField.tsx';

export function AdminRaces() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);

  const races = useQuery({ queryKey: ['admin-races'], queryFn: () => api.listRaces() });
  const foreninger = useQuery({ queryKey: ['foreninger'], queryFn: () => api.listForeninger() });
  const fById = new Map((foreninger.data ?? []).map((f) => [f.id, f.name] as const));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">Regattaer</h1>
        <button
          className="bg-sky-600 hover:bg-sky-500 px-3 py-2 rounded text-sm font-semibold"
          onClick={() => setShowNew(!showNew)}
        >
          {showNew ? 'Avbryt' : '+ Ny regatta'}
        </button>
      </div>

      {showNew && (
        <NewRaceForm
          onDone={() => {
            setShowNew(false);
            qc.invalidateQueries({ queryKey: ['admin-races'] });
          }}
        />
      )}

      {races.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : (
        <ul className="space-y-2">
          {(races.data ?? []).map((r) => (
            <li key={r.id}>
              <Link
                to={`/admin/races/${r.id}`}
                className="block bg-slate-900/40 hover:bg-slate-900 border border-slate-800 rounded-xl p-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate">{r.name}</div>
                    <div className="text-xs text-slate-400 font-mono">
                      {new Date(r.startTime).toLocaleString('no-NO', {
                        dateStyle: 'medium',
                        timeStyle: 'short',
                        timeZone: 'Europe/Oslo',
                      })}
                    </div>
                    {r.foreningId && fById.get(r.foreningId) && (
                      <div className="text-xs text-sky-300/80">⚓ {fById.get(r.foreningId)}</div>
                    )}
                    {r.registrationDeadline && (
                      <div className="text-xs text-slate-500">
                        Påmeldingsfrist:{' '}
                        {new Date(r.registrationDeadline).toLocaleString('no-NO', {
                          dateStyle: 'short',
                          timeStyle: 'short',
                          timeZone: 'Europe/Oslo',
                        })}
                      </div>
                    )}
                  </div>
                  <StatusBadge status={r.status} />
                </div>
              </Link>
            </li>
          ))}
          {races.data?.length === 0 && (
            <li className="text-sm text-slate-500 p-4 text-center">
              Ingen regattaer. Opprett den første.
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const colors: Record<string, string> = {
    scheduled: 'bg-sky-600/30 text-sky-300',
    postponed: 'bg-amber-600/30 text-amber-300',
    started: 'bg-emerald-600/30 text-emerald-300',
    finished: 'bg-slate-700/40 text-slate-400',
    cancelled: 'bg-red-700/30 text-red-400',
  };
  const labels: Record<string, string> = {
    scheduled: 'Planlagt',
    postponed: 'Utsatt',
    started: 'Pågår',
    finished: 'Ferdig',
    cancelled: 'Avlyst',
  };
  return (
    <span className={`text-xs px-2 py-1 rounded ${colors[status] ?? 'bg-slate-700'}`}>
      {labels[status] ?? status}
    </span>
  );
}

function NewRaceForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [venue, setVenue] = useState('Grimstad havn');
  const [description, setDescription] = useState('');
  const [registrationUrl, setRegistrationUrl] = useState('');
  const [startMode, setStartMode] = useState<StartMode>('fixed');
  const [deadlineDate, setDeadlineDate] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('');
  const [classIds, setClassIds] = useState<string[]>([]);
  const [foreningId, setForeningId] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const classes = useQuery({ queryKey: ['classes', false], queryFn: () => api.listClasses(false) });
  const foreninger = useQuery({ queryKey: ['foreninger'], queryFn: () => api.listForeninger() });

  const mut = useMutation({
    mutationFn: async () => {
      if (!date || !time) throw new Error('Fyll inn dato og tidspunkt');
      const local = new Date(`${date}T${time}`);
      if (isNaN(local.getTime())) throw new Error('Ugyldig dato/tid');

      let registrationDeadline: string | null = null;
      if (deadlineDate && deadlineTime) {
        const dl = new Date(`${deadlineDate}T${deadlineTime}`);
        if (isNaN(dl.getTime())) throw new Error('Ugyldig påmeldingsfrist');
        registrationDeadline = dl.toISOString();
      } else if (deadlineDate || deadlineTime) {
        throw new Error('Sett både dato og tid for påmeldingsfrist, eller la begge stå tomme');
      }

      return api.createRace({
        name,
        startTime: local.toISOString(),
        venue: venue || undefined,
        description: description || undefined,
        registrationDeadline,
        registrationUrl: registrationUrl.trim() || null,
        startMode,
        allowedClassIds: classIds,
        foreningId: foreningId || undefined,
      });
    },
    onSuccess: onDone,
    onError: (e: ApiError | Error) => setErr(e.message),
  });

  function toggleClass(id: string) {
    setClassIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mut.mutate();
      }}
      className="mb-4 bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold">Ny regatta</h2>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">
          {err}
        </div>
      )}

      <div>
        <label className="block text-xs text-slate-400 mb-1">Navn</label>
        <input
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          placeholder="Grimstad Påskeregatta 2026"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Forening</label>
        <select
          value={foreningId}
          onChange={(e) => setForeningId(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        >
          <option value="">— Velg forening —</option>
          {(foreninger.data ?? []).map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-slate-400 mb-1">Dato</label>
          <input
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </div>
        <div>
          <label className="block text-xs text-slate-400 mb-1">Starttid (lokal)</label>
          <input
            type="time"
            required
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </div>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Sted</label>
        <input
          value={venue}
          onChange={(e) => setVenue(e.target.value)}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <StartModeField value={startMode} onChange={setStartMode} />
      <div>
        <label className="block text-xs text-slate-400 mb-1">Påmeldingslenke (valgfritt)</label>
        <input
          type="url"
          value={registrationUrl}
          onChange={(e) => setRegistrationUrl(e.target.value)}
          placeholder="https://…"
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
        <p className="text-xs text-slate-500 mt-1">
          Vises som «Påmelding»-knapp på den offentlige regattalista.
        </p>
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Beskrivelse (valgfritt)</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>

      <div className="border-t border-slate-800 pt-3">
        <label className="block text-xs text-slate-400 mb-1">
          Påmeldingsfrist for registrar (valgfritt)
        </label>
        <p className="text-xs text-slate-500 mb-2">
          Etter denne tiden kan kun admin etterregistrere båter. La feltene stå tomme for å holde
          åpent helt frem til regattaen starter.
        </p>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="date"
            value={deadlineDate}
            onChange={(e) => setDeadlineDate(e.target.value)}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
          <input
            type="time"
            value={deadlineTime}
            onChange={(e) => setDeadlineTime(e.target.value)}
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          />
        </div>
      </div>

      <div className="border-t border-slate-800 pt-3">
        <label className="block text-xs text-slate-400 mb-1">Tillatte klasser</label>
        <p className="text-xs text-slate-500 mb-2">
          Velg én eller flere klasser. Hvis du ikke velger noen er regattaen åpen for alle klasser.
        </p>
        <div className="grid grid-cols-2 gap-1">
          {(classes.data ?? []).map((c) => {
            const checked = classIds.includes(c.id);
            return (
              <label
                key={c.id}
                className={`flex items-center gap-2 px-2 py-1.5 border rounded cursor-pointer text-sm ${
                  checked
                    ? 'border-sky-600 bg-sky-900/30 text-sky-200'
                    : 'border-slate-700 bg-slate-950 text-slate-300'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleClass(c.id)}
                  className="accent-sky-500"
                />
                {c.name}
              </label>
            );
          })}
        </div>
      </div>

      <button
        type="submit"
        disabled={mut.isPending}
        className="w-full bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
      >
        {mut.isPending ? 'Oppretter…' : 'Opprett regatta'}
      </button>
    </form>
  );
}
